import { STORAGE_QUOTA_EXCEEDED_CODE, CONTENT_DAMAGED_CODE } from '@papan/types'

/**
 * The error a FAILED extract job's result becomes — the same codes the inline route answers with.
 *
 * Carry the CODE, not just the sentence: the inline route maps `quotaExceeded` to 413 + STORAGE_QUOTA_EXCEEDED and the
 * SPA branches on that code (`storageRefusalText`); a bare Error read as the generic "Extract failed" on the >50 MB
 * archives that take this path, the likeliest to hit the cap. A busy database (#1290) is the server's own mapped
 * answer (`apiErrorResponse`): its client-safe sentence, code and status, as the inline route's 503.
 */
export function extractJobFailureError(r: ExtractJobFailure): ExtractFailureError {
  const partial = extractPartialOf(r)
  if (r.busy) {
    const busy = new Error(r.busy.body.error || 'This database is busy — retry shortly.') as ExtractFailureError
    busy.code = r.busy.body.code; busy.status = r.busy.status
    if (r.busy.body.landing) busy.landing = true
    if (partial) busy.partial = partial
    return busy
  }
  const err = new Error(r.error || 'Extraction failed') as ExtractFailureError
  if (r.quotaExceeded) {
    err.code = STORAGE_QUOTA_EXCEEDED_CODE
    err.status = 413
  }
  if (r.reservedName) { err.code = 'RESERVED_NAME'; err.status = 400 }
  // #1459 review — the inline route's other coded answers too: every archive with a database entry now runs in the worker,
  // so without these the drive lost the database refusals' own wording (and the write refusal's 403)
  if (r.engineDbInvalid) { err.code = 'ENGINE_DB_INVALID'; err.status = 422 }
  if (r.engineDbOwned) { err.code = 'ENGINE_DB_OWNED'; err.status = 409 }
  if (r.writeBlocked) err.status = 403
  // #1397 G2: the archive's stored copy is damaged — the same coded answer the inline route gives
  if (r.contentDamaged) { err.code = CONTENT_DAMAGED_CODE; err.status = 409 }
  if (partial) err.partial = partial
  return err
}

/** What an extract refused part-way already wrote — the drive offers to undo exactly that (#1430 (2)). */
export interface ExtractPartial {
  fileIds: string[]
  /** What the undo bins: the created items whose parent the extract did not create (an older server: absent). */
  topFileIds?: string[]
  versioned: Array<{ fileId: string; previousVersionId: string; versionId?: string }>
  destinationFolderId: string | null
}
export type ExtractFailureError = Error & { code?: string; status?: number; landing?: boolean; partial?: ExtractPartial }
export interface ExtractJobFailure {
  error?: string
  quotaExceeded?: boolean
  reservedName?: boolean
  engineDbInvalid?: boolean
  engineDbOwned?: boolean
  writeBlocked?: boolean
  contentDamaged?: boolean
  busy?: { status: number; body: { error?: string; code?: string; landing?: boolean } }
  createdFileIds?: string[]
  topCreatedFileIds?: string[]
  versioned?: ExtractPartial['versioned']
  createdFolderId?: string | null
}

/** The worker result's written part, as the inline route's `partial` — absent when nothing was written. */
export function extractPartialOf(r: ExtractJobFailure): ExtractPartial | undefined {
  const fileIds = r.createdFileIds ?? []
  const versioned = r.versioned ?? []
  const destinationFolderId = r.createdFolderId ?? null
  if (fileIds.length === 0 && versioned.length === 0 && !destinationFolderId) return undefined
  return { fileIds, ...(r.topCreatedFileIds ? { topFileIds: r.topCreatedFileIds } : {}), versioned, destinationFolderId }
}
